import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import {
  Image, Users, Camera, Compass, User, LogOut, Globe, Building2
} from 'lucide-react';

// ============================================
// 5-ITEM PRIMARY NAVIGATION
// Memories | People | Capture | Explore | Profile
// Story Wall accessible as main nav item
// ============================================

const navItems = [
  { path: '/memories', label: 'Memories', icon: Image },
  { path: '/people', label: 'People', icon: Users },
  { path: '/camera', label: 'Capture', icon: Camera, isCapture: true },
  { path: '/story-wall', label: 'Story Wall', icon: Globe },
  { path: '/explore', label: 'Explore', icon: Compass },
  { path: '/organization', label: 'Organization', icon: Building2 },
  { path: '/profile', label: 'Profile', icon: User },
];

export default function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/memories') {
      return ['/memories', '/my-memories', '/life-timeline', '/timeline', '/life-map',
              '/life-chapters', '/photos', '/gallery', '/uploads', '/on-this-day',
              '/moments', '/albums', '/home', '/dashboard', '/weekly-recap'].some(p => location.pathname.startsWith(p))
             || location.pathname === '/home' || location.pathname === '/dashboard';
    }
    if (path === '/people') {
      return ['/people', '/family-timelines', '/lifestart', '/relationship-graph',
              '/life-circles'].some(p => location.pathname.startsWith(p));
    }
    if (path === '/camera') {
      return location.pathname.startsWith('/camera') || location.pathname.startsWith('/edit');
    }
    if (path === '/story-wall') {
      return ['/story-wall', '/stories', '/life-stories', '/feed'].some(p => location.pathname.startsWith(p));
    }
    if (path === '/explore') {
      return ['/explore', '/memory-map', '/travel-journal', '/legacies',
              '/documentaries', '/ai-companion', '/story-generator', '/life-graph',
              '/books', '/life-movie', '/voice-memories',
              '/create', '/location-story', '/explorer'].some(p => location.pathname.startsWith(p));
    }
    if (path === '/profile') {
      return ['/profile', '/settings', '/billing'].some(p => location.pathname.startsWith(p));
    }
    if (path === '/organization') {
      return location.pathname.startsWith('/organization');
    }
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <>
      {/* ================================================ */}
      {/* DESKTOP SIDEBAR — Clean 5-item nav               */}
      {/* ================================================ */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-[#141414] border-r border-[#2A2A2A]/40 flex-col z-40">
        <div className="px-6 h-16 flex items-center border-b border-[#1C1C1C]/60">
          <Link to="/memories" data-testid="nav-logo" className="inline-flex max-w-[188px] pr-4">
            <Logo size="xs" showTagline={false} />
          </Link>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.filter(i => !i.isCapture).map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  active
                    ? 'bg-primary text-white'
                    : 'text-stone-400 hover:bg-[#0F0F0F] hover:text-white'
                }`}
              >
                <Icon className={`w-[18px] h-[18px] ${active ? 'text-white' : 'text-stone-400'}`} />
                <span className="font-body">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Capture CTA */}
        <div className="px-4 pb-3">
          <button
            onClick={() => navigate('/camera')}
            className="w-full flex items-center justify-center gap-2.5 px-4 py-3 bg-primary hover:bg-primary-light text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
            data-testid="nav-capture-btn"
          >
            <Camera className="w-4 h-4" />
            <span className="font-body">Capture Moment</span>
          </button>
        </div>

        {/* User footer */}
        <div className="px-4 py-4 border-t border-[#1C1C1C]/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-full bg-[#2A2A2A] flex items-center justify-center text-stone-300 text-xs font-bold flex-shrink-0">
                {(user?.full_name || user?.name)?.[0]?.toUpperCase() || 'U'}
              </div>
              <span className="text-sm text-stone-200 font-medium truncate">{user?.full_name || user?.name || 'User'}</span>
            </div>
            <button
              onClick={handleLogout}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-stone-400 hover:text-stone-300 hover:bg-[#1C1C1C] transition-colors"
              data-testid="nav-logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ================================================ */}
      {/* MOBILE TOP BAR                                   */}
      {/* ================================================ */}
      <header className="lg:hidden fixed top-0 left-0 right-0 glass border-b border-[#2A2A2A]/30 z-40" style={{ paddingTop: 'var(--sat)' }}>
        <div className="h-14 flex items-center justify-between px-4">
          <Link to="/memories">
            <Logo size="xs" showTagline={false} />
          </Link>
        </div>
      </header>

      {/* Spacer: pushes page content below the fixed mobile header + safe area */}
      <div className="lg:hidden" style={{ height: 'calc(3.5rem + var(--sat))' }} aria-hidden="true" />

      {/* ================================================ */}
      {/* MOBILE BOTTOM NAVIGATION — 5 items               */}
      {/* Capture centered & emphasized                    */}
      {/* ================================================ */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 glass border-t border-[#2A2A2A]/30 z-40 safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.filter(i => i.path !== '/explore').map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);

            if (item.isCapture) {
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid="mobile-nav-capture"
                  className="flex flex-col items-center justify-center gap-0.5 w-16"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center -mt-5 shadow-lg transition-transform active:scale-95 ${
                    active ? 'bg-primary ring-4 ring-primary/15' : 'bg-primary'
                  }`}>
                    <Camera className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-[10px] font-body font-semibold text-primary">{item.label}</span>
                </Link>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center justify-center gap-0.5 py-1 w-14 rounded-xl transition-colors ${
                  active ? 'text-primary' : 'text-stone-400'
                }`}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-primary' : 'text-stone-400'}`} />
                <span className={`text-[10px] font-body font-medium ${active ? 'text-primary' : 'text-stone-400'}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
